<script lang="ts">
import SolidLock03 from '@docue/docue-ui-v2/icons/SolidLock03.vue'

import type { Payload as CreatePaymentIntentPayload } from '~/composables/useChargebee'

import { isValidationError } from '~/utils/api/errors'
import poweredByStripeImageSrc from '~/assets/images/powered-by-stripe.svg?url'

export interface SepaPaymentDetails {
  firstName: string
  lastName: string
  email: string
}
</script>

<script setup lang="ts">
defineProps<{
  disabled?: boolean
}>()

const modelValue = defineModel<SepaPaymentDetails>({ required: true })
const isBankAccountUnusable = ref(false)

const iban = ref('')

const isIbanValid = computed(() => !!(iban.value.trim().length && !isBankAccountUnusable.value))

watch(iban, (iban, oldIban) => {
  if (isBankAccountUnusable.value && iban !== oldIban)
    isBankAccountUnusable.value = false
}, { deep: true })

const { createPaymentIntent } = useChargebee()

const { t } = useI18n()

const isValid = computed(() => !!(modelValue.value.firstName.trim().length && modelValue.value.lastName.trim().length && modelValue.value.email.trim().length && isIbanValid.value))

const {
  execute,
  error,
} = useAsyncState(async (payload: CreatePaymentIntentPayload) => await createPaymentIntent({
  iban: iban.value.trim(),
  firstName: modelValue.value.firstName.trim(),
  lastName: modelValue.value.lastName.trim(),
  email: modelValue.value.email.trim(),
  payload,
}), undefined, { immediate: false, throwError: true })

const isErrorWithCode = (err: unknown): err is { code: string } => typeof err === 'object' && err !== null && 'code' in err

whenever(error, (err) => {
  console.error(err)

  if (isErrorWithCode(err) && err.code === 'bank_account_unusable')
    isBankAccountUnusable.value = true
})

const { activeWorkspaceId } = useAuth()

const {
  mutateAsync: createSepaPaymentSource,
  error: createSepaPaymentSourceError,
  isPending: isCreateSepaPaymentSourcePending,
} = useMutationChargebee.createSepaPaymentSource()

const save = async () => {
  if (isCreateSepaPaymentSourcePending.value || !activeWorkspaceId.value || !isValid.value)
    return

  await createSepaPaymentSource({
    workspaceId: activeWorkspaceId.value,
    payload: {
      iban: iban.value.trim(),
      first_name: modelValue.value.firstName.trim(),
      last_name: modelValue.value.lastName.trim(),
      email: modelValue.value.email.trim(),
      replace: true,
    },
  })
}

whenever(createSepaPaymentSourceError, (err) => {
  console.error(err)

  if (isValidationError(err) && !!err?.data?.validationErrors?.iban)
    isBankAccountUnusable.value = true
})

defineExpose({
  isValid,
  createPaymentIntent: (params: Parameters<typeof execute>[1]) => execute(0, params),
  save,
})
</script>

<template>
  <div class="grid grid-cols-2 gap-3 @container">
    <DTField
      :label="t('first-name')"
      class="col-span-full @[30rem]:col-span-1"
    >
      <DTFieldInput
        v-model.trim="modelValue.firstName"
        required
        :disabled="disabled"
      />
    </DTField>
    <DTField
      :label="t('last-name')"
      class="col-span-full @[30rem]:col-span-1"
    >
      <DTFieldInput
        v-model.trim="modelValue.lastName"
        required
        :disabled="disabled"
      />
    </DTField>
    <DTField
      class="col-span-full"
      label="IBAN"
      :error="isBankAccountUnusable ? t('billing.sepa.invalidIban') : undefined"
    >
      <DTFieldInput
        v-model.trim="iban"
        required
        :invalid="isBankAccountUnusable"
        :disabled="disabled"
      />
    </DTField>
    <DTField
      :label="t('billing.invoicingEmail')"
      class="col-span-full"
    >
      <DTFieldInput
        v-model.trim="modelValue.email"
        required
        type="email"
        :disabled="disabled"
      />
    </DTField>

    <p class="col-span-full text-xs text-gray-600">
      {{ t('billing.sepa.mandate') }}
    </p>

    <div class="col-span-full flex items-center justify-end gap-3">
      <span class="inline-flex items-center gap-2 text-xs text-gray-400">
        <SolidLock03 class="size-3" />
        {{ capitalize(t('billing.securePayment')) }}
      </span>
      <img :src="poweredByStripeImageSrc">
    </div>
  </div>
</template>
